import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Subscription } from "@/types/subscription";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserSubscriptionEditor } from "./UserSubscriptionEditor";
import { Loader2, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthProvider";

interface User {
  id: string;
  email: string;
  created_at: string;
  subscription?: Subscription;
}

export function UsersList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const { session } = useAuth();

  // Use Edge Function to fetch users - now without admin verification
  const { data: users, isLoading, error, refetch } = useQuery({
    queryKey: ["users-list"],
    queryFn: async () => {
      try {
        if (!session?.access_token) {
          console.error("No access token available");
          throw new Error("Authentication required");
        }

        console.log("Calling get-admin-users with token");

        // Call our edge function to get users
        const response = await supabase.functions.invoke('get-admin-users', {
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        });
        
        if (response.error) {
          console.error("Error response from get-admin-users function:", response.error);
          throw new Error(response.error.message || "Error fetching users");
        }

        const data = response.data;
        
        if (!data || !data.users || !Array.isArray(data.users)) {
          console.error("Invalid response format from get-admin-users function:", data);
          throw new Error("Invalid response from server");
        }

        // Log the users we received
        console.log("Users fetched successfully:", data.users.length);

        // Get subscriptions for all users
        const { data: subscriptions } = await supabase
          .from("subscriptions")
          .select("*");
        
        console.log("Subscriptions fetched:", subscriptions?.length || 0);

        // Map subscriptions to users
        const usersWithSubscriptions = data.users.map(user => {
          const userSubscription = subscriptions?.find(sub => sub.user_id === user.id);
          return {
            ...user,
            subscription: userSubscription
          };
        });

        return usersWithSubscriptions;
      } catch (error: any) {
        console.error("Error in user query:", error);
        throw new Error(error.message || "Failed to fetch users");
      }
    },
    enabled: !!session?.access_token,
    retry: 1,  // Limit retries to prevent excessive error messages
  });

  // Filter users based on search term
  const filteredUsers = users?.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id.includes(searchTerm)
  );

  const handleEditUser = (userId: string) => {
    setEditingUserId(userId);
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
  };

  // Handle retry logic
  const handleRetry = () => {
    toast.info("Retrying user fetch...");
    refetch();
  };

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 p-6 rounded-md shadow border border-red-200">
          <div className="flex items-start mb-3">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-medium text-red-800">Error loading users</h3>
              <p className="text-red-600 mt-1">{(error as Error).message}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Make sure you are authenticated with an active session.
            </p>
            <Button 
              variant="outline" 
              onClick={handleRetry} 
              className="flex items-center space-x-1"
            >
              <Loader2 className="h-3 w-3 mr-1" />
              <span>Retry</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Users Management</h2>
        <div className="relative w-64">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
          <p className="ml-2">Loading users...</p>
        </div>
      ) : (
        <div className="bg-white rounded-md shadow overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">User ID</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers && filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-mono text-xs">{user.id}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {user.subscription?.plan || "No Plan"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEditUser(user.id)}
                        >
                          Edit Subscription
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      {searchTerm ? "No users found matching your search" : "No users found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* User Subscription Editor Dialog */}
      {editingUserId && (
        <UserSubscriptionEditor
          userId={editingUserId}
          open={!!editingUserId}
          onOpenChange={handleCancelEdit}
        />
      )}
    </div>
  );
}
